import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Last line of defence: a render error anywhere below this shows a recoverable
 * screen instead of a blank page. Deliberately styled with plain tokens and no
 * component imports, so it still renders if the design system is what broke.
 */
export class AppErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
     
    console.error('Unhandled render error:', error, info.componentStack);
  }

  private readonly handleReload = (): void => {
    window.location.assign('/');
  };

  override render(): ReactNode {
    const { error } = this.state;

    if (!error) return this.props.children;

    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas px-4">
        <div className="w-full max-w-md rounded-xl border border-border bg-surface p-6 text-center shadow-card">
          <h1 className="text-lg font-semibold text-content">Something went wrong</h1>
          <p className="mt-2 text-sm text-content-muted">
            The page hit an unexpected error. Reloading usually clears it.
          </p>
          {import.meta.env.DEV ? (
            <pre className="mt-4 max-h-48 overflow-auto rounded-lg bg-surface-sunken p-3 text-left text-xs text-danger">
              {error.message}
            </pre>
          ) : null}
          <button
            type="button"
            onClick={this.handleReload}
            className="mt-5 inline-flex h-9 items-center justify-center rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-hover"
          >
            Back to the dashboard
          </button>
        </div>
      </div>
    );
  }
}
